/**
 * How long this document is kept, and why (FR-25 – FR-27, D22 – D24) — on the
 * document's page, for everyone who may read it:
 *
 * - the **retention class** and the day it may go (`retain_until`);
 * - **why**: the anchor the floors count from (the date on the document, else
 *   its upload), every statutory floor its type carries with the day it ends
 *   and its **citation**, and the organisation's rule above them;
 * - a live document past its date is **flagged** — kept until someone
 *   decides, never removed (FR-26); an expired one says when its file went and
 *   why;
 * - an administrator's **override**, with its reason (FR-27): offered to
 *   `settings:can_update`, **behind step-up**, at or above the floor — below it
 *   is the service's `below_floor`, said at the date;
 * - W9 (FR-25 as amended, D46): a sibling's document no rule of the
 *   organisation's matches is **kept by its app** — no date, so it is never due
 *   and never flagged here; the panel says whose it is and that the
 *   organisation can set a rule for that app's documents, still shows the
 *   floor, and still offers the override (which gives it a date).
 *
 * Package rules (ADR-0010): the host lends its step-up; nothing here knows a
 * session or a router.
 */
import { Alert, Badge, Button, DescriptionList, Detail, Dialog, EmptyState, Input, Textarea, formatDate, formatDateTime } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import {
  MAX_RETENTION_REASON,
  appName,
  belowFloorOf,
  isStepUpCancelled,
  keptByOf,
  mayOverrideRetention,
  withStepUp,
  type ProDocClient,
  type ProDocDocument,
  type StepUpAsk,
  type Verb,
} from './prodoc-client'
import { errorText } from './hooks'

export interface RetentionPanelProps {
  client: ProDocClient
  doc: ProDocDocument
  can: (resource: string, verb: Verb) => boolean
  /** The host's step-up (T6.25); without it the override is not offered. */
  requestStepUp?: StepUpAsk
  /** The record as the service answered the override. */
  onChanged: (doc: ProDocDocument) => void
}

const ANCHOR: Record<string, string> = {
  document_date: 'the date on the document',
  upload_date: 'its upload',
  upload: 'its upload',
}

const EXPIRY: Record<string, string> = {
  retention: 'the organisation’s retention period ended',
  erasure: 'a personal-data erasure request',
}

type RetentionFacts = Pick<ProDocDocument, 'status' | 'retention_flagged_at'> & Partial<Pick<ProDocDocument, 'retain_until' | 'retention_basis'>>

/**
 * Whether the document is past its date and kept only until someone decides
 * (FR-26). A document kept by its app has no date, so it is never past one —
 * whatever a flag stamped before W9 still says.
 */
export const isFlagged = (doc: RetentionFacts) =>
  doc.status !== 'expired' && Boolean(doc.retention_flagged_at) && !keptByOf(doc)

export function RetentionPanel({ client, doc, can, requestStepUp, onChanged }: RetentionPanelProps) {
  const [overriding, setOverriding] = useState(false)
  const basis = doc.retention_basis ?? null
  const floors = basis?.floors ?? []
  const canOverride = mayOverrideRetention(can) && Boolean(requestStepUp) && doc.status !== 'expired'
  // W9 (D46): the sibling app that keeps this document's life — no date of ProDoc's.
  const kept = keptByOf(doc)
  const keeper = kept ? appName(kept) : null

  if (doc.status === 'expired') {
    return (
      <div className="space-y-3">
        <Alert tone="info">
          Its file was removed {doc.expired_at ? formatDateTime(doc.expired_at) : ''}
          {doc.expiry_reason ? ` — ${EXPIRY[doc.expiry_reason] ?? doc.expiry_reason}` : ''}. Its record and its history are kept; nothing brings the file
          back.
        </Alert>
        {(doc.retain_until || keeper) && (
          <DescriptionList>
            <Detail label="It was kept until" value={doc.retain_until ? formatDate(doc.retain_until) : `Kept by ${keeper}`} />
          </DescriptionList>
        )}
      </div>
    )
  }

  if (!doc.retain_until && !keeper) {
    return (
      <EmptyState
        variant="outline"
        title="Not worked out yet"
        description="How long this document is kept is worked out from its type and date by the service; the daily retention job fills it in for a document stored before it did."
      />
    )
  }

  const counted = basis?.anchor ? `Counted from ${ANCHOR[basis.anchor_kind ?? ''] ?? 'its anchor'}, ${formatDate(basis.anchor)}.` : undefined

  return (
    <div className="space-y-4">
      {keeper && (
        // FR-25 as amended: the app manages this document's life; a rule of the organisation's would give it a date.
        <Alert tone="info">
          Kept by {keeper} — your organisation can set a rule for {keeper}’s documents.
        </Alert>
      )}
      {isFlagged(doc) && (
        <Alert tone="warning">
          Past its retention date since {formatDateTime(doc.retention_flagged_at!)}. It is still here, because a live document is never removed — archive it to
          let the retention job remove its file, or keep it longer with an override.
        </Alert>
      )}
      <DescriptionList columns={2}>
        {keeper ? (
          <Detail
            label="Kept until"
            value={`Kept by ${keeper}`}
            hint={`No date of ProDoc’s: ${keeper} manages this document’s life, so it is never flagged or removed here.`}
          />
        ) : (
          <Detail
            label="Kept until"
            value={formatDate(doc.retain_until)}
            hint={doc.retention_override_until ? 'Set by an administrator’s override.' : 'The longer of the law’s minimum and the organisation’s rule.'}
          />
        )}
        <Detail label="Retention class" value={doc.retention_class ? doc.retention_class.replace(/_/g, ' ') : null} emptyLabel="Not set" />
        <Detail
          label={keeper ? 'Never before' : 'The law’s minimum'}
          value={doc.retention_floor_until ? formatDate(doc.retention_floor_until) : null}
          emptyLabel="None — no statute names this type"
          hint={keeper ? `The floor still holds: an override or an erasure cannot go before it.${counted ? ` ${counted}` : ''}` : counted}
        />
        <Detail
          label="The organisation’s rule"
          value={basis?.org_rule?.months ? `${basis.org_rule.months} months${basis.org_rule.owner_app ? ` (for ${basis.org_rule.owner_app})` : ''}` : null}
          emptyLabel={keeper ? `None for ${keeper}’s documents` : 'None — the law’s minimum applies'}
        />
      </DescriptionList>

      {floors.length > 0 && (
        <div>
          <p className="text-foreground mb-1 text-sm font-medium">Why — the floors its type carries</p>
          <ul className="space-y-1" aria-label="Statutory floors">
            {floors.map((f) => (
              <li key={`${f.rule}:${f.until}`} className="text-sm">
                <span className="text-foreground">Until {formatDate(f.until)}</span> <span className="text-muted-foreground">— {f.citation || f.rule}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {doc.retention_override_until && (
        <p className="text-muted-foreground text-sm">
          Overridden{doc.retention_overridden_at ? ` ${formatDateTime(doc.retention_overridden_at)}` : ''}
          {doc.retention_override_reason ? `: “${doc.retention_override_reason}”` : '.'}
        </p>
      )}

      {canOverride && (
        <Button variant="outline" size="sm" onClick={() => setOverriding(true)}>
          {doc.retention_override_until ? 'Change the override…' : 'Keep it longer…'}
        </Button>
      )}

      {canOverride && <OverrideDialog open={overriding} client={client} doc={doc} requestStepUp={requestStepUp!} onClose={() => setOverriding(false)} onDone={(next) => { setOverriding(false); onChanged(next) }} />}
    </div>
  )
}

function OverrideDialog({ open, client, doc, requestStepUp, onClose, onDone }: { open: boolean; client: ProDocClient; doc: ProDocDocument; requestStepUp: StepUpAsk; onClose: () => void; onDone: (doc: ProDocDocument) => void }) {
  const [until, setUntil] = useState('')
  const [reason, setReason] = useState('')
  const [clearing, setClearing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [dateError, setDateError] = useState<string | null>(null)
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!open) return
    setUntil(doc.retention_override_until ?? doc.retain_until ?? '')
    setReason('')
    setClearing(false)
    setError('')
    setDateError(null)
    setHidden(false)
  }, [open, doc])

  const floor = doc.retention_floor_until
  const belowLocally = Boolean(floor && until && !clearing && until < floor)

  const save = async () => {
    setBusy(true)
    setError('')
    setDateError(null)
    try {
      const next = await withStepUp(
        (assertion) => client.documents.setRetention(doc.id, { retain_until: clearing ? null : until, reason }, { stepUpAssertion: assertion }),
        async (request) => {
          // The step-up dialog stands alone while it is open; this one waits, its fields kept.
          setHidden(true)
          try {
            return await requestStepUp(request)
          } finally {
            setHidden(false)
          }
        },
        {
          title: clearing ? 'Clear the retention override' : 'Override this document’s retention',
          what: clearing
            ? `“${doc.title}” goes back to the organisation’s rule and the law’s minimum.`
            : `“${doc.title}” will be kept until ${until ? formatDate(until) : 'the date you chose'}. The reason is recorded with your name.`,
        },
        true,
      )
      onDone(next)
    } catch (e) {
      const below = belowFloorOf(e)
      if (below) setDateError(`Before the law’s minimum${below.until ? ` of ${formatDate(below.until)}` : ''}${below.citation ? ` (${below.citation})` : ''}.`)
      else setError(isStepUpCancelled(e) ? 'Nothing was changed — the re-verification was cancelled.' : errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open && !hidden} onClose={onClose} title="Retention override" error={error || null}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!busy && reason.trim() && (clearing || (until && !belowLocally))) void save()
        }}
      >
        <p className="text-muted-foreground text-sm">
          Keep this document longer than its rule says — a litigation hold, an audit. It can never go before the law’s minimum
          {floor ? `, ${formatDate(floor)}` : ''}.
        </p>
        {!clearing && (
          <Input
            type="date"
            label="Keep until"
            value={until}
            min={floor ?? undefined}
            onChange={(e) => {
              setUntil(e.target.value)
              setDateError(null)
            }}
            error={dateError ?? (belowLocally ? `Before the law’s minimum of ${formatDate(floor!)}.` : null)}
          />
        )}
        <Textarea
          label="Reason"
          rows={3}
          maxLength={MAX_RETENTION_REASON}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          hint="Recorded in the document’s history with your name."
        />
        {doc.retention_override_until && (
          <Button type="button" variant="link" onClick={() => setClearing(!clearing)}>
            {clearing ? 'Set a date instead' : 'Clear the override instead'}
          </Button>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} loadingText="Saving…" disabled={!reason.trim() || (!clearing && (!until || belowLocally))}>
            {clearing ? 'Re-verify and clear' : 'Re-verify and save'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/** A small badge for a header: past its date. Never for a document kept by its app, which has none. */
export function RetentionBadge({ doc }: { doc: RetentionFacts }) {
  if (!isFlagged(doc)) return null
  return (
    <Badge tone="warning" size="sm">
      Past retention date
    </Badge>
  )
}
